import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Divider,
  Container,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logo from "../../Assets/logo.png";

const PublicNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const isActive = (path) => location.pathname === path;

  // تأثير الـ scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuItems = [
    { to: "/", label: "الرئيسية" },
    { to: "/about-app", label: "عن التطبيق" },
    { to: "/contact-us", label: "تواصل معنا" },
  ];

  return (
    <>
      {/* Top Navbar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled
            ? "rgba(243, 111, 33, 0.95)"
            : "linear-gradient(135deg, rgba(243, 111, 33, 0.98) 0%, rgba(230, 81, 0, 0.98) 100%)",
          backdropFilter: "blur(20px)",
          borderBottom: scrolled ? "none" : "1px solid rgba(255,255,255,0.1)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: scrolled
            ? "0 4px 30px rgba(0, 0, 0, 0.15)"
            : "0 2px 20px rgba(0, 0, 0, 0.1)",
          py: { xs: 0, sm: scrolled ? 0 : 0.5 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 } }}>
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              py: { xs: scrolled ? 0.5 : 1, sm: scrolled ? 1 : 1.5 },
              px: { xs: 0.5, sm: 1, md: 2 },
              minHeight: { xs: 56, sm: 64 },
              transition: "all 0.3s ease",
            }}
          >
            {/* Logo - تصميم احترافي محسّن */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                gap: { xs: 1, sm: 1.5, md: 2 },
                position: "relative",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "scale(1.05)",
                  "& .logo-img": {
                    filter: "drop-shadow(0 6px 16px rgba(255,255,255,0.4))",
                    transform: "rotate(-5deg)",
                  },
                  "& .logo-text": {
                    letterSpacing: "1px",
                  },
                },
              }}
            >
              {/* Logo Container مع تأثيرات */}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Glow Effect خلف اللوجو */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: { xs: -4, sm: -6, md: -8 },
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)",
                    filter: { xs: "blur(8px)", sm: "blur(10px)", md: "blur(12px)" },
                    opacity: scrolled ? 0.6 : 0.8,
                    transition: "all 0.3s ease",
                  }}
                />

                {/* White circular background behind logo */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    borderRadius: "50%",
                    bgcolor: "white",
                    p: { xs: scrolled ? 0.3 : 0.5, sm: scrolled ? 0.5 : 0.8, md: scrolled ? 0.5 : 1 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: { xs: "0 3px 10px rgba(0,0,0,0.08)", sm: "0 4px 15px rgba(0,0,0,0.08)", md: "0 6px 20px rgba(0,0,0,0.08)" },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="ريستو كاش"
                    className="logo-img"
                    sx={{
                      height: scrolled 
                        ? { xs: 28, sm: 34, md: 44 } 
                        : { xs: 36, sm: 40, md: 54 },
                      width: "auto",
                      borderRadius: "50%",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                </Box>

                {/* Animated Ring حول اللوجو */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: { xs: -3, sm: -4, md: -6 },
                    borderRadius: "50%",
                    border: { xs: "1.5px solid rgba(255,255,255,0.25)", sm: "2px solid rgba(255,255,255,0.25)" },
                    opacity: 0,
                    transition: "all 0.3s ease",
                    animation: "pulse-ring 2s ease-in-out infinite",
                    "@keyframes pulse-ring": {
                      "0%, 100%": {
                        transform: "scale(1)",
                        opacity: 0.3,
                      },
                      "50%": {
                        transform: "scale(1.15)",
                        opacity: 0,
                      },
                    },
                  }}
                />
              </Box>

              {/* Logo Text */}
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  flexDirection: "column",
                  gap: { sm: 0.2, md: 0.3 },
                }}
              >
                <Typography
                  variant="h6"
                  className="logo-text"
                  sx={{
                    fontWeight: 900,
                    fontSize: scrolled
                      ? { sm: "0.95rem", md: "1.1rem", lg: "1.35rem" }
                      : { sm: "1.05rem", md: "1.2rem", lg: "1.5rem" },
                    textShadow: "0 3px 10px rgba(0,0,0,0.35)",
                    letterSpacing: "0.5px",
                    lineHeight: 1,
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    background: "linear-gradient(135deg, #ffffff 0%, #ffe0b2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -2,
                      left: 0,
                      width: "100%",
                      height: "2px",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                    },
                    "&:hover::after": {
                      opacity: 1,
                    },
                  }}
                >
                  ريستو كاش
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { sm: "0.6rem", md: "0.7rem", lg: "0.75rem" },
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 600,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    textShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    opacity: scrolled ? 0 : 1,
                    maxHeight: scrolled ? 0 : "20px",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                  }}
                >
                  Restaurant POS
                </Typography>
              </Box>

              {/* Badge - تطبيق\/حصري */}
              <Box
                sx={{
                  position: "absolute",
                  top: { xs: -4, sm: -6, md: -8 },
                  right: { xs: -6, sm: -8, md: -12 },
                  background:
                    "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
                  color: "white",
                  fontSize: { xs: "0.55rem", sm: "0.6rem", md: "0.65rem" },
                  fontWeight: 700,
                  px: { xs: 0.6, sm: 0.8, md: 1 },
                  py: { xs: 0.2, sm: 0.25, md: 0.3 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  boxShadow: { xs: "0 1px 4px rgba(74, 222, 128, 0.3)", sm: "0 2px 8px rgba(74, 222, 128, 0.4)" },
                  animation: "badge-pulse 2s ease-in-out infinite",
                  "@keyframes badge-pulse": {
                    "0%, 100%": {
                      transform: "scale(1) rotate(-5deg)",
                    },
                    "50%": {
                      transform: "scale(1.1) rotate(-8deg)",
                    },
                  },
                }}
              >
                تطبيق
              </Box>
            </Box>

            {/* Desktop Menu */}
            <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 0.5 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.to}
                  component={Link}
                  to={item.to}
                  sx={{
                    px: 3,
                    py: 1.2,
                    color: "white",
                    fontWeight: isActive(item.to) ? 700 : 500,
                    fontSize: "1rem",
                    position: "relative",
                    borderRadius: 2,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      borderRadius: 2,
                      background: isActive(item.to)
                        ? "rgba(255,255,255,0.2)"
                        : "transparent",
                      transition: "all 0.3s ease",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 6,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: isActive(item.to) ? "60%" : "0%",
                      height: "3px",
                      background:
                        "linear-gradient(90deg, transparent, white, transparent)",
                      borderRadius: "2px",
                      transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    },
                    "&:hover": {
                      background: "rgba(255,255,255,0.15)",
                      transform: "translateY(-2px)",
                      "&::after": {
                        width: "60%",
                      },
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* Desktop Buttons */}
            <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 2 }}>
              <Button
                component={Link}
                to="/signup"
                variant="outlined"
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.6)",
                  borderWidth: 2,
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  borderRadius: 3,
                  fontSize: "0.95rem",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                    transform: "translateX(-100%)",
                    transition: "transform 0.3s ease",
                  },
                  "&:hover": {
                    borderColor: "white",
                    background: "rgba(255,255,255,0.15)",
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
                    "&::before": {
                      transform: "translateX(0)",
                    },
                  },
                }}
              >
                إنشاء حساب
              </Button>
              <Button
                component={Link}
                to="/login"
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #ffffff 0%, #fff3e0 100%)",
                  color: "#f36f21",
                  px: 3.5,
                  py: 1.2,
                  fontWeight: 700,
                  borderRadius: 3,
                  fontSize: "0.95rem",
                  boxShadow: "0 4px 15px rgba(255,255,255,0.3)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #ffffff 0%, #ffe0b2 100%)",
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 25px rgba(255,255,255,0.4)",
                  },
                }}
              >
                تسجيل الدخول
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <Box sx={{ display: { xs: "flex", lg: "none" } }}>
              <IconButton
                onClick={toggleMenu}
                sx={{
                  color: "white",
                  background: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(10px)",
                  p: { xs: 1, sm: 1.2 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "rgba(255,255,255,0.2)",
                    transform: "rotate(90deg)",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  },
                }}
              >
                {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="top"
        open={isMenuOpen}
        onClose={toggleMenu}
        sx={{
          "& .MuiDrawer-paper": {
            background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            pt: { xs: 8, sm: 9 },
            borderRadius: { xs: "0 0 16px 16px", sm: "0 0 20px 20px", md: "0 0 24px 24px" },
            boxShadow: { xs: "0 6px 24px rgba(0,0,0,0.25)", sm: "0 8px 32px rgba(0,0,0,0.3)", md: "0 10px 40px rgba(0,0,0,0.3)" },
          },
        }}
      >
        <Box
          sx={{ width: "100vw", p: { xs: 2, sm: 2.5, md: 3 } }}
          role="presentation"
          onClick={toggleMenu}
          onKeyDown={toggleMenu}
        >
          <List sx={{ mb: { xs: 1.5, sm: 2 } }}>
            {menuItems.map((item, index) => (
              <ListItem
                key={item.to}
                button
                component={Link}
                to={item.to}
                sx={{
                  borderRadius: { xs: 2, sm: 2.5, md: 3 },
                  mb: { xs: 1, sm: 1.25, md: 1.5 },
                  py: { xs: 1.5, sm: 1.75, md: 2 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  background: isActive(item.to)
                    ? "rgba(255,255,255,0.25)"
                    : "rgba(255,255,255,0.05)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  animation: `slideIn 0.3s ease ${index * 0.1}s both`,
                  "@keyframes slideIn": {
                    from: {
                      opacity: 0,
                      transform: "translateX(-20px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateX(0)",
                    },
                  },
                  "&:hover": {
                    background: "rgba(255,255,255,0.2)",
                    transform: { xs: "translateX(4px)", sm: "translateX(6px)", md: "translateX(8px)" },
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.to) ? 700 : 500,
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.15rem" },
                  }}
                />
              </ListItem>
            ))}
          </List>
          <Divider
            sx={{
              my: { xs: 2, sm: 2.5, md: 3 },
              borderColor: "rgba(255,255,255,0.2)",
              borderWidth: 1,
            }}
          />
          <Box display="flex" flexDirection="column" gap={{ xs: 1.5, sm: 1.75, md: 2 }}>
            <Button
              component={Link}
              to="/signup"
              variant="outlined"
              fullWidth
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.6)",
                borderWidth: 2,
                py: { xs: 1.5, sm: 1.65, md: 1.8 },
                fontWeight: 700,
                fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
                borderRadius: { xs: 2.5, sm: 2.75, md: 3 },
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "white",
                  background: "rgba(255,255,255,0.2)",
                  transform: "scale(1.02)",
                },
              }}
            >
              إنشاء حساب
            </Button>
            <Button
              component={Link}
              to="/login"
              variant="contained"
              fullWidth
              sx={{
                background: "linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)",
                color: "#667eea",
                py: { xs: 1.5, sm: 1.65, md: 1.8 },
                fontWeight: 700,
                fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
                borderRadius: { xs: 2.5, sm: 2.75, md: 3 },
                boxShadow: { xs: "0 4px 12px rgba(255,255,255,0.2)", sm: "0 5px 16px rgba(255,255,255,0.25)", md: "0 6px 20px rgba(255,255,255,0.25)" },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: "linear-gradient(135deg, #ffffff 0%, #e8edff 100%)",
                  transform: "scale(1.02)",
                  boxShadow: { xs: "0 6px 18px rgba(255,255,255,0.3)", sm: "0 7px 22px rgba(255,255,255,0.35)", md: "0 8px 25px rgba(255,255,255,0.35)" },
                },
              }}
            >
              تسجيل الدخول
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default PublicNavbar;
